'use client';

import { useState } from 'react';
import Link from 'next/link';
import CountryFAQ from '@/components/country/CountryFAQ';
import GfSectionMark from '@/components/common/GfSectionMark';
import GfArticleCards from '@/components/common/GfArticleCards';
import GfSubnav from '@/components/common/GfSubnav';
import GfVerticalHero from '@/components/common/GfVerticalHero';
import GfSteps from '@/components/common/GfSteps';
import GfCta from '@/components/common/GfCta';
import GfProviderCard from '@/components/common/GfProviderCard';
import GfPromoCodes from '@/components/country/GfPromoCodes';
import { makeCountryDisplay } from '@/lib/content/declension';
import { t } from '@/i18n/translations';

// Provider `vertical` (already set per provider in Sanity) → transport mode bucket.
// Grouping is driven by this tag, not by which curation array the provider sits in,
// so a flight provider (Aviasales) never lands under "public transport" again.
const MODE_OF = {
    transfers: 'transfer',
    car_rental: 'rental',
    bike_rental: 'rental',
    boat_rental: 'rental',
    trains: 'transit',
    buses: 'transit',
    flights: 'flight',
};
const MODE_ORDER = ['transfer', 'rental', 'transit', 'flight'];

export default function TransportPageContent({
    content,
    transportArticles = [],
    promotions = [],
    background,
    transferProviders = [],
    rentalProviders = [],
    publicTransportProviders = [],
    country = 'montenegro',
    countryName,
    airports = [],
}) {
    const lang = content?.lang || 'ru';
    const isRu = lang === 'ru';
    const { cName, cCase } = makeCountryDisplay(countryName, country, isRu);
    const sp = t(lang, 'servicePage');        // общие тексты сервис-страниц
    const vp = t(lang, 'transportPage');      // специфика транспорта

    const features = Array.isArray(content?.features) ? content.features : [];
    const steps = Array.isArray(content?.steps) ? content.steps : [];
    const faqItems = (Array.isArray(content?.faq) ? content.faq : []).map((it) => ({ question: it.q ?? it.question, answer: it.a ?? it.answer }));

    const labelOf = (m) => vp.mode[m] || m;

    // Flatten the three curation arrays into one list, dedupe by code, and tag each
    // provider with its transport mode (derived from the provider's `vertical`).
    const providers = [];
    const seenCodes = new Set();
    // Аренда первой: Localrent — 76% денег сайта, а стоял третьим из семи (обход 01.10.2026).
    for (const p of [...rentalProviders, ...transferProviders, ...publicTransportProviders]) {
        if (!p?.code || seenCodes.has(p.code)) continue;
        seenCodes.add(p.code);
        providers.push({ ...p, mode: MODE_OF[p.vertical] || 'transfer' });
    }
    const modes = MODE_ORDER.filter((m) => providers.some((p) => p.mode === m));
    const [activeMode, setActiveMode] = useState('all');
    const shownProviders = activeMode === 'all' ? providers : providers.filter((p) => p.mode === activeMode);

    const providerName = (p) => p.title;
    const providerDesc = (p) => (isRu ? p.descriptionRu : p.descriptionEn) || (isRu ? p.descriptionEn : p.descriptionRu);

    const has = {
        features: features.length > 0,
        providers: providers.length > 0,
        airports: Array.isArray(airports) && airports.length > 0,
        how: steps.length > 0,
        promos: promotions?.length > 0,
        faq: faqItems.length > 0,
        articles: transportArticles?.length > 0,
    };

    const anchors = [
        has.features && { href: '#features', label: vp.anchorFeatures },
        has.providers && { href: '#providers', label: vp.anchorProviders },
        has.airports && { href: '#airports', label: vp.anchorAirports },
        has.how && { href: '#how', label: vp.anchorHow },
        has.promos && { href: '#promocodes', label: sp.anchor.discounts },
        has.faq && { href: '#faq', label: sp.anchor.faq },
        has.articles && { href: '#articles', label: sp.anchor.articles },
    ].filter(Boolean);

    const ProviderGrid = ({ list, vertical }) => (
        <div className="gf-grid gf-grid-center">
            {list.map((p) => (
                <GfProviderCard
                    key={p.code}
                    name={providerName(p)}
                    logo={p.logoUrl}
                    logoAlt={p.logoAlt}
                    description={providerDesc(p)}
                    stamp={labelOf(p.mode)}
                    className="pc-whole"
                >
                    <Link className="visit" href={`/${lang}/${country}/${vertical}/${p.code}`}>{sp.details}</Link>
                </GfProviderCard>
            ))}
        </div>
    );

    return (
        <div className="gf-root">
            <GfVerticalHero background={background} title={content?.title} sub={content?.description}>
                {has.providers && <a className="pri" href="#providers">{vp.heroChoose} <b>→</b></a>}
                {has.how && <a className="sec" href="#how">{vp.heroHow}</a>}
            </GfVerticalHero>

            <GfSubnav lang={lang} leaf={vp.leaf} anchors={anchors}
                crumbs={[cName && { href: `/${lang}/${country}`, label: cName }]} />

            {has.features && (
                <section className="gf-sec" id="features">
                    <GfSectionMark eyebrow={vp.featuresEyebrow} title={vp.featuresTitle} />
                    {content?.featuresSubtitle && <p className="gf-lead">{content.featuresSubtitle}</p>}
                    <div className="gf-scen">
                        {features.map((f, i) => (
                            <article className="sc" key={i}>
                                <span className="n">{`// 0${i + 1}`}</span>
                                <h3>{f.title}</h3>
                                {f.desc && <p>{f.desc}</p>}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            {has.providers && (
                <section className="gf-sec" id="providers" style={{ paddingTop: has.features ? 0 : undefined }}>
                    <GfSectionMark eyebrow={vp.providersEyebrow} tag={providers.length} title={vp.providersTitle} />
                    <p className="gf-lead">{vp.providersLead}</p>
                    {modes.length > 1 && (
                        <div className="gf-ops" role="group" aria-label={vp.modeAria}>
                            <button type="button" className={`all ${activeMode === 'all' ? 'on' : ''}`} aria-pressed={activeMode === 'all'} onClick={() => setActiveMode('all')}>{vp.modeAll}</button>
                            {modes.map((m) => (
                                <button type="button" key={m} className={activeMode === m ? 'on' : ''} aria-pressed={activeMode === m} onClick={() => setActiveMode(m)}>{labelOf(m)}</button>
                            ))}
                        </div>
                    )}
                    <ProviderGrid list={shownProviders} vertical="transport" />
                </section>
            )}

            {has.airports && (
                <section className="gf-sec" id="airports" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.airportsEyebrow} tag={airports.length} title={vp.airportsTitle} />
                    <p className="gf-lead">{vp.airportsLead}</p>
                    <div className="gf-grid gf-grid-center">
                        {airports.map((a) => (
                            <GfProviderCard
                                key={a.iata}
                                name={(a.title || a.name || '').trim()}
                                description={vp.airportCardDesc}
                                stamp={String(a.iata || '').toUpperCase()}
                                href={`/${lang}/${country}/airport/${String(a.iata || '').toLowerCase()}`}
                            >
                                <span className="visit">{sp.details}</span>
                            </GfProviderCard>
                        ))}
                    </div>
                </section>
            )}

            {has.how && (
                <section className="gf-sec" id="how" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.guide} tag={`${steps.length} ${sp.stepsWord(steps.length)}`} title={content?.stepsTitle || vp.stepsTitle} />
                    {content?.stepsSubtitle && <p className="gf-lead">{content.stepsSubtitle}</p>}
                    <GfSteps steps={steps} />
                </section>
            )}

            {has.promos && (
                <section className="gf-sec" id="promocodes" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.discounts} tag={sp.tag.current} title={vp.promosTitle} />
                    <p className="gf-lead">{vp.promoLead}</p>
                    <GfPromoCodes promotions={promotions} lang={lang} country={country} />
                </section>
            )}

            {has.faq && (
                <section className="gf-sec" id="faq" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.qa} tag={sp.tag.faq} title={sp.popularQuestions} />
                    {content?.faqSubtitle && <p className="gf-lead">{content.faqSubtitle}</p>}
                    <CountryFAQ faqItems={faqItems} lang={lang} variant="gf" />
                    {content?.faqLink?.href && content?.faqLink?.text && (
                        <div style={{ marginTop: 26 }}><Link className="gf-alllink" href={content.faqLink.href}>{content.faqLink.text}</Link></div>
                    )}
                </section>
            )}

            {has.articles && (
                <section className="gf-sec" id="articles" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.resources} tag={sp.tag.blog} title={vp.articlesTitle} allHref={`/${lang}/blog/category/transport`} allLabel={sp.allArticles} />
                    <p className="gf-lead">{vp.articlesLead}</p>
                    <GfArticleCards posts={transportArticles} lang={lang} variant="bento" bucket />
                </section>
            )}

            <GfCta
                eyebrow={vp.ctaEyebrow}
                title={vp.ctaTitle(cName ? (isRu ? cCase('dative') : cName) : '')}
            >
                {has.providers && <a className="gf-btn" href="#providers">{vp.ctaBtn}</a>}
            </GfCta>
        </div>
    );
}
